import React, { useRef, useState, useCallback, memo } from 'react';
import { motion } from 'framer-motion';

/**
 * Magic Bento Card Component - Optimized for Performance
 * Based on ReactBits: https://reactbits.dev/components/magic-bento
 * Features: Spotlight, Click Stars Effect (magnetism disabled for perf)
 */

interface StarParticle {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
}

interface MagicCardProps {
  children: React.ReactNode;
  className?: string;
  gradientColor?: string;
  gradientOpacity?: number;
  gradientSize?: number;
  magnetism?: boolean;
}

const MagicCardComponent: React.FC<MagicCardProps> = ({
  children,
  className = '',
  gradientColor = '#7c3aed',
  gradientOpacity = 0.3,
  gradientSize = 400,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const lastUpdateRef = useRef<number>(0);
  const [mousePosition, setMousePosition] = useState({ x: -1000, y: -1000 });
  const [isHovered, setIsHovered] = useState(false);
  const [stars, setStars] = useState<StarParticle[]>([]);

  // Throttled mouse move (16ms = ~60fps)
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const now = performance.now();
    if (now - lastUpdateRef.current < 16) return;
    
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    
    rafRef.current = requestAnimationFrame(() => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      lastUpdateRef.current = now;
    });
  }, []);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setMousePosition({ x: -1000, y: -1000 });
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // Click stars effect (reduced to 5 stars)
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newStars: StarParticle[] = Array.from({ length: 5 }, (_, i) => ({
      id: Date.now() + i,
      x,
      y,
      dx: (Math.random() - 0.5) * 80,
      dy: -40 - Math.random() * 40,
    }));
    
    setStars(prev => [...prev, ...newStars]);

    setTimeout(() => {
      setStars(prev => prev.filter(s => !newStars.find(ns => ns.id === s.id)));
    }, 600);
  }, []);

  const opacityHex = Math.round(gradientOpacity * 255).toString(16).padStart(2, '0');

  return (
    <motion.div
      ref={cardRef}
      className={`relative overflow-visible rounded-xl cursor-pointer ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      style={{ willChange: 'transform' }}
    >
      {/* Main card container */}
      <div 
        className="relative overflow-hidden rounded-xl h-full"
        style={{ willChange: isHovered ? 'opacity' : 'auto' }}
      >
        {/* Spotlight gradient - GPU accelerated */}
        <div
          className="pointer-events-none absolute inset-0 z-10"
          style={{
            opacity: isHovered ? 1 : 0,
            background: `radial-gradient(${gradientSize}px circle at ${mousePosition.x}px ${mousePosition.y}px, ${gradientColor}${opacityHex}, transparent 50%)`,
            transition: 'opacity 0.2s ease-out',
            willChange: 'opacity',
          }}
        />
        
        {/* Border glow */}
        <div
          className="pointer-events-none absolute inset-0 rounded-xl z-20"
          style={{
            opacity: isHovered ? 1 : 0,
            background: `radial-gradient(${gradientSize * 0.7}px circle at ${mousePosition.x}px ${mousePosition.y}px, ${gradientColor}bb, ${gradientColor}44 35%, transparent 55%)`,
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'exclude',
            WebkitMaskComposite: 'xor',
            padding: '1.5px',
            transition: 'opacity 0.2s ease-out',
          }}
        />
        
        {/* Content */}
        <div className="relative z-30">{children}</div>
      </div>

      {/* Click Stars Effect */}
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="pointer-events-none absolute z-50"
          initial={{ x: star.x, y: star.y, scale: 0, opacity: 1 }}
          animate={{ 
            x: star.x + star.dx,
            y: star.y + star.dy,
            scale: [0, 1.2, 0],
            opacity: [1, 0.8, 0],
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill={gradientColor}>
            <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
          </svg>
        </motion.div>
      ))}
    </motion.div>
  );
};

export const MagicCard = memo(MagicCardComponent);
export default MagicCard;
